import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { pressingFetch, clearPressingSession, setPresserSession, stashPressingAdminToken, getKioskToken, clearKioskToken } from './AuthComponent';
import { useI18n } from './i18n/I18nContext';

export default function PressingPortalLogin() {
    const navigate = useNavigate();
    const { setLanguage } = useI18n();
    const [pin, setPin]             = useState('');
    const [portalType, setPortalType] = useState('');
    const [error, setError]         = useState('');
    const [busy, setBusy]           = useState(false);
    const [menuOpen, setMenuOpen]   = useState(false);
    const [confirmRevoke, setConfirmRevoke] = useState(false);

    useEffect(() => {
        document.body.style.backgroundColor = '#0d2240';
        return () => { document.body.style.backgroundColor = ''; };
    }, []);

    useEffect(() => {
        let token = sessionStorage.getItem('pressing_admin_token');
        if (!token) {
            const adminToken = localStorage.getItem('operations_token');
            if (adminToken) {
                stashPressingAdminToken();
                token = sessionStorage.getItem('pressing_admin_token');
            }
        }
        if (!token) {
            // Kiosk fallback: if a persistent kiosk token exists, promote it
            // into the active session so the PIN login page can use it.
            const kiosk = getKioskToken();
            if (kiosk) {
                sessionStorage.setItem('pressing_admin_token', kiosk);
                token = kiosk;
            }
        }
        if (!token) navigate('/login', { replace: true });
    }, [navigate]);

    const handleRevokeKiosk = () => {
        clearPressingSession();
        clearKioskToken();
        localStorage.removeItem('operations_token');
        window.location.href = '/login';
    };

    const kioskEnrolled = Boolean(getKioskToken());

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!portalType) return setError('Please select a portal type.');
        if (!/^\d{4,5}$/.test(pin)) return setError('PIN must be 4 or 5 digits.');
        setBusy(true);
        try {
            const res = await pressingFetch('/api/pressing/verify-pin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pin, portal_type: portalType }),
            });
            if (res.ok) {
                const data = await res.json();
                setPresserSession(data);
                // Apply portal-user's language preference to the I18n context
                // so the rest of the portal renders in their language.
                setLanguage(data?.language === 'es' ? 'es' : 'en');
                const PORTAL_ROUTES = {
                    picker:    '/picking-portal/active',
                    presser:   '/pressing-portal/active',
                    packing:   '/packing-portal/active',
                    store:     '/store-pickup-portal/active',
                    warehouse: '/warehouse-portal/home',   // the tool menu (Style Check, Add Inventory, Cycle Count, Stock Take)
                    bulk_shipping: '/bulk-shipping-portal/active',
                };
                navigate(PORTAL_ROUTES[portalType] ?? '/pressing-portal/active', { replace: true });
            } else {
                const err = await res.json().catch(() => ({}));
                setError(err.detail || 'Invalid PIN. Please try again.');
            }
        } catch (ex) {
            if (!ex.message?.includes('No pressing admin token')) {
                setError('Network error. Please try again.');
            }
        } finally {
            setBusy(false);
        }
    };

    const portalOptions = [
        { type: 'picker',    label: 'Picking',      icon: '🛒' },
        { type: 'presser',   label: 'Pressing',     icon: '👕' },
        { type: 'packing',   label: 'Packing',      icon: '📦' },
        { type: 'store',     label: 'Store Pickup', icon: '🏬' },
        { type: 'warehouse', label: 'Warehouse',    icon: '🏭' },
        { type: 'bulk_shipping', label: 'Bulk Shipping', icon: '🚚' },
    ];

    return (
        <div className="pl-page">
            {/* Gear menu in the top-right corner */}
            {kioskEnrolled && (
                <div className="pl-gear-wrap">
                    <button
                        type="button"
                        aria-label="Kiosk settings"
                        className="pl-gear-btn"
                        onClick={() => setMenuOpen(o => !o)}
                        data-testid="kiosk-gear"
                    >
                        ⚙
                    </button>
                    {menuOpen && (
                        <div className="pl-gear-menu" onClick={e => e.stopPropagation()}>
                            <button
                                type="button"
                                className="pl-gear-menu__item"
                                onClick={() => { setMenuOpen(false); setConfirmRevoke(true); }}
                                data-testid="revoke-kiosk"
                            >
                                Disable kiosk on this device
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Confirmation modal */}
            {confirmRevoke && (
                <div className="pl-modal-backdrop" onClick={() => setConfirmRevoke(false)}>
                    <div className="pl-modal" onClick={e => e.stopPropagation()}>
                        <h2 className="pl-modal__title">Disable kiosk mode?</h2>
                        <p className="pl-modal__body">
                            This device will be removed from kiosk mode. To use the portal again,
                            an administrator will need to sign in and tap <strong>Enter Portals</strong> again.
                        </p>
                        <div className="pl-modal__actions">
                            <button type="button" className="pl-modal__btn" onClick={() => setConfirmRevoke(false)}>
                                Cancel
                            </button>
                            <button type="button" className="pl-modal__btn pl-modal__btn--danger"
                                    onClick={handleRevokeKiosk} data-testid="confirm-revoke-kiosk">
                                Disable kiosk
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="pl-header">
                <img src="/sp-ball-icon.png" alt="Soccer Post" className="pl-icon" />
                <h1 className="pl-title">Ops Portal</h1>
                <p className="pl-subtitle">Select a portal and enter your PIN to continue</p>
            </div>

            <div className="pl-card">
                <form onSubmit={handleSubmit}>
                    {/* Portal type selection */}
                    <div className="pl-field">
                        <label className="pl-label">Portal</label>
                        <div className="pl-portal-btns">
                            {portalOptions.map(opt => (
                                <button
                                    key={opt.type}
                                    type="button"
                                    disabled={busy}
                                    onClick={() => setPortalType(opt.type)}
                                    className={`pl-portal-btn${portalType === opt.type ? ' pl-portal-btn--active' : ''}`}
                                >
                                    <span className="pl-portal-btn__icon">{opt.icon}</span>
                                    <span>{opt.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="pl-field pl-field--last">
                        <label className="pl-label">4 or 5 Digit PIN</label>
                        <input
                            type="tel"
                            inputMode="numeric"
                            pattern="[0-9]{4,5}"
                            maxLength={5}
                            value={pin}
                            onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 5))}
                            placeholder=""
                            autoComplete="off"
                            disabled={busy}
                            className="pl-input pl-input--pin"
                            style={{ backgroundColor: busy ? '#f6f6f7' : '#ffffff' }}
                            onFocus={e => { e.target.style.borderColor = '#1a4fa0'; e.target.style.boxShadow = '0 0 0 3px rgba(26,79,160,0.2)'; }}
                            onBlur={e => { e.target.style.borderColor = '#c9cccf'; e.target.style.boxShadow = 'none'; }}
                        />
                    </div>

                    {error && <div className="pl-error">{error}</div>}

                    <button
                        type="submit"
                        disabled={busy || !portalType || pin.length < 4 || pin.length > 5}
                        className="pl-submit"
                    >
                        {busy ? 'Verifying…' : 'Enter Portal'}
                    </button>
                </form>
            </div>
        </div>
    );
}
